import { useEffect, useRef, useState } from "react";
import operatorApiService from "../../../services/operator-api.service";
import type { BpsLogPoint } from "../utils/types";
import {
  formatLocalDateTimeForRequest,
  parseDateTimeLocal,
} from "../utils/datetime";
import { trimToRealtimeWindow } from "../utils/realtime";
import { getRealtimeAxisEndMs } from "../utils/realtimeAxis";

type Args = {
  vehicleId: number | null;
  isRealtime: boolean;
  appliedStartInput: string;
  appliedEndInput: string;
  appliedFetchNonce?: number;
  realtimeWindowMinutes: number;
  delayOffsetMs: number;
  realtimeRightEdgeFallbackLagMs: number;
  maxPointsForUi: number;
  disableUiCap?: boolean;
  initialStartInput: string;
  initialEndInput: string;
  setStartInput: (v: string) => void;
  setEndInput: (v: string) => void;
  setAppliedStartInput: (v: string) => void;
  setAppliedEndInput: (v: string) => void;
};

// bpslogデータを期間指定/リアルタイムで取得し、必要に応じてトリムや再取得を行う。
export const useBpsLogData = ({
  vehicleId,
  isRealtime,
  appliedStartInput,
  appliedEndInput,
  appliedFetchNonce,
  realtimeWindowMinutes,
  delayOffsetMs,
  realtimeRightEdgeFallbackLagMs,
  maxPointsForUi,
  disableUiCap,
  initialStartInput,
  initialEndInput,
  setStartInput,
  setEndInput,
  setAppliedStartInput,
  setAppliedEndInput,
}: Args) => {
  const appliedFetchNonceKey =
    typeof appliedFetchNonce === "number" ? appliedFetchNonce : 0;
  const [bpslogData, setBpsLogData] = useState<BpsLogPoint[]>([]);
  const [rangePendingRequestCount, setRangePendingRequestCount] = useState(0);
  const rangePendingRequestCountRef = useRef(0);
  const [pollPendingRequestCount, setPollPendingRequestCount] = useState(0);
  const pollPendingRequestCountRef = useRef(0);
  const isMountedRef = useRef(true);
  const rangeAbortRef = useRef<AbortController | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);
  const pollInFlightRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 期間取得リクエスト数をインクリメントし、loading表示用stateへ反映する。
  const beginRangeRequest = () => {
    rangePendingRequestCountRef.current += 1;
    if (isMountedRef.current) {
      setRangePendingRequestCount(rangePendingRequestCountRef.current);
    }
  };

  // 期間取得リクエスト数をデクリメントし、loading表示用stateへ反映する。
  const endRangeRequest = () => {
    rangePendingRequestCountRef.current = Math.max(
      0,
      rangePendingRequestCountRef.current - 1,
    );
    if (isMountedRef.current) {
      setRangePendingRequestCount(rangePendingRequestCountRef.current);
    }
  };

  // リアルタイムpollリクエスト数をインクリメントし、loading表示用stateへ反映する。
  const beginPollRequest = () => {
    pollPendingRequestCountRef.current += 1;
    if (isMountedRef.current) {
      setPollPendingRequestCount(pollPendingRequestCountRef.current);
    }
  };

  // リアルタイムpollリクエスト数をデクリメントし、loading表示用stateへ反映する。
  const endPollRequest = () => {
    pollPendingRequestCountRef.current = Math.max(
      0,
      pollPendingRequestCountRef.current - 1,
    );
    if (isMountedRef.current) {
      setPollPendingRequestCount(pollPendingRequestCountRef.current);
    }
  };

  const fetchSeqRef = useRef(0);
  const lastModemIdRef = useRef<number | null>(null);
  const tooManyAlertedRef = useRef(false);
  const fetchErrorAlertedRef = useRef(false);
  const stickyRealtimeAxisEndMsRef = useRef<number | null>(null);
  const lastSuccessfulPeriodRangeRef = useRef<{ start: string; end: string }>({
    start: initialStartInput,
    end: initialEndInput,
  });

  const periodCacheRef = useRef<{
    vehicleId: number;
    delayOffsetMs: number;
    disableUiCap: boolean;
    appliedFetchNonce: number;
    startMs: number;
    endMs: number;
    data: BpsLogPoint[];
  } | null>(null);

  // 数値を[min..max]にクランプする。
  const clamp = (v: number, min: number, max: number) => {
    return Math.max(min, Math.min(max, v));
  };

  const isAbortError = (err: unknown) => {
    const anyErr = err as any;
    return (
      anyErr?.code === "ERR_CANCELED" ||
      anyErr?.name === "CanceledError" ||
      anyErr?.name === "AbortError"
    );
  };

  const cancelRequests = () => {
    fetchSeqRef.current += 1;

    rangeAbortRef.current?.abort();
    rangeAbortRef.current = null;

    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
    pollInFlightRef.current = false;

    rangePendingRequestCountRef.current = 0;
    pollPendingRequestCountRef.current = 0;
    if (isMountedRef.current) {
      setRangePendingRequestCount(0);
      setPollPendingRequestCount(0);
    }
  };

  // 取得済みデータを指定ミリ秒レンジでフィルタする（start/endは順不同可）。
  const filterByRangeMs = (
    data: BpsLogPoint[],
    startMs: number,
    endMs: number,
  ) => {
    const minMs = Math.min(startMs, endMs);
    const maxMs = Math.max(startMs, endMs);
    const out: BpsLogPoint[] = [];
    for (let i = 0; i < data.length; i++) {
      const t = new Date(data[i].timestamp).getTime();
      if (!Number.isFinite(t)) continue;
      if (t < minMs) continue;
      if (t > maxMs) continue;
      out.push(data[i]);
    }
    return out;
  };

  useEffect(() => {
    lastModemIdRef.current = null;
    tooManyAlertedRef.current = false;
    fetchErrorAlertedRef.current = false;
    periodCacheRef.current = null;
    stickyRealtimeAxisEndMsRef.current = null;
  }, [
    vehicleId,
    isRealtime,
    appliedStartInput,
    appliedEndInput,
    appliedFetchNonceKey,
    realtimeWindowMinutes,
    delayOffsetMs,
    disableUiCap,
  ]);

  useEffect(() => {
    if (!bpslogData.length) return;
    const last = bpslogData[bpslogData.length - 1];
    if (typeof last?.modem_id === "number") {
      lastModemIdRef.current = last.modem_id;
    }
  }, [bpslogData]);

  // データ過多の警告を1回だけ表示する。
  const alertTooManyPointsOnce = (maxRowsFromApi?: number | null) => {
    if (tooManyAlertedRef.current) return;
    tooManyAlertedRef.current = true;
    const limit =
      typeof maxRowsFromApi === "number" && Number.isFinite(maxRowsFromApi)
        ? maxRowsFromApi
        : maxPointsForUi;
    window.alert(
      `取得データが多すぎます。\n期間を短くしてください。 (上限: ${limit}件)`,
    );
  };

  const alertFetchErrorOnce = (status?: number | null) => {
    if (fetchErrorAlertedRef.current) return;
    fetchErrorAlertedRef.current = true;

    const detail =
      typeof status === "number" && Number.isFinite(status)
        ? ` (HTTP ${status})`
        : "";

    window.alert(`データの取得に失敗しました。${detail}`);
  };

  // 直近の成功した期間へ入力値/適用値を巻き戻す。
  const rollbackPeriodRangeToLastSuccessful = () => {
    if (isRealtime) return;
    const prev = lastSuccessfulPeriodRangeRef.current;
    if (!prev?.start || !prev?.end) return;
    setStartInput(prev.start);
    setEndInput(prev.end);
    setAppliedStartInput(prev.start);
    setAppliedEndInput(prev.end);
  };

  // リアルタイム時のみ、表示窓の範囲へトリムしつつ右端のsticky情報も更新する。
  const trimToRealtimeWindowIfNeeded = (data: BpsLogPoint[], nowMs: number) => {
    if (!isRealtime) return data;
    const { endMs, usedData } = getRealtimeAxisEndMs(
      data,
      nowMs,
      realtimeRightEdgeFallbackLagMs,
      stickyRealtimeAxisEndMsRef.current,
    );
    if (usedData) stickyRealtimeAxisEndMsRef.current = endMs;
    return trimToRealtimeWindow(data, endMs, realtimeWindowMinutes);
  };

  // リアルタイム取得は表示窓より少し広めに取り、表示幅変更やデータ遅延時でも左側が一時的に空かないようにする。
  const getRealtimeFetchBufferMs = (nowMs: number) => {
    const base = Math.max(10_000, realtimeRightEdgeFallbackLagMs * 2);

    const endInfo = getRealtimeAxisEndMs(
      bpslogData,
      nowMs,
      realtimeRightEdgeFallbackLagMs,
      stickyRealtimeAxisEndMsRef.current,
    );
    const endMs = endInfo?.endMs;
    const ageMs =
      typeof endMs === "number" && Number.isFinite(endMs)
        ? Math.max(0, nowMs - endMs)
        : 0;

    // 観測した遅延に安全マージンを足す。
    const needed = ageMs + 2_000;

    // 異常停止時に過剰な範囲にならないよう上限を設ける。
    return Math.min(120_000, Math.max(base, needed));
  };

  useEffect(() => {
    if (vehicleId === null) return;
    const now = new Date();
    const realtimeFetchBufferMs = getRealtimeFetchBufferMs(now.getTime());
    const startAt = isRealtime
      ? new Date(
          now.getTime() -
            realtimeWindowMinutes * 60_000 -
            realtimeFetchBufferMs,
        )
      : parseDateTimeLocal(appliedStartInput);
    if (!startAt) return;

    const endAt = isRealtime ? now : parseDateTimeLocal(appliedEndInput);
    if (!endAt) return;

    // 開始より終了が前の場合は、同時刻に丸める
    const safeEnd = endAt.getTime() < startAt.getTime() ? startAt : endAt;
    const disableUiCapKey = Boolean(disableUiCap);

    // ホイール/パン/ズーム等で連続取得が起きるため、古いHTTPはabortして積み上がりを防ぐ。
    rangeAbortRef.current?.abort();
    const abortController = new AbortController();
    rangeAbortRef.current = abortController;

    const fetchData = async () => {
      const seq = ++fetchSeqRef.current;
      try {
        if (!isRealtime) {
          const reqStartAt = startAt;
          const reqEndAt = safeEnd;
          const reqStartMs = reqStartAt.getTime();
          const reqEndMs = reqEndAt.getTime();
          if (!Number.isFinite(reqStartMs) || !Number.isFinite(reqEndMs))
            return;

          const cache = periodCacheRef.current;
          const sameContext =
            cache &&
            cache.vehicleId === vehicleId &&
            cache.delayOffsetMs === delayOffsetMs &&
            cache.disableUiCap === disableUiCapKey &&
            cache.appliedFetchNonce === appliedFetchNonceKey;

          if (
            sameContext &&
            cache.startMs <= Math.min(reqStartMs, reqEndMs) &&
            cache.endMs >= Math.max(reqStartMs, reqEndMs)
          ) {
            setBpsLogData(filterByRangeMs(cache.data, reqStartMs, reqEndMs));
            lastSuccessfulPeriodRangeRef.current = {
              start: appliedStartInput,
              end: appliedEndInput,
            };
            return;
          }

          const spanMs = Math.max(1, Math.abs(reqEndMs - reqStartMs));
          // 先読みバッファでパン中の再取得頻度を下げつつ、上限で複数日閲覧時の過大取得を防ぐ。
          const bufferMs = clamp(spanMs * 0.5, 10 * 60_000, 24 * 60 * 60_000);
          const fetchStartMs = Math.min(reqStartMs, reqEndMs) - bufferMs;
          const fetchEndMs = Math.max(reqStartMs, reqEndMs) + bufferMs;

          const startIso = formatLocalDateTimeForRequest(
            new Date(fetchStartMs),
          );
          const endIso = formatLocalDateTimeForRequest(new Date(fetchEndMs));

          beginRangeRequest();
          try {
            const data = await operatorApiService.doGetBpsLogData({
              vehicleId: vehicleId,
              start: startIso,
              end: endIso,
              maxRows: maxPointsForUi,
              delayOffsetMs,
              noCap: disableUiCapKey ? 1 : undefined,
              signal: abortController.signal,
            });

            if (seq !== fetchSeqRef.current) return;

            if (Array.isArray(data)) {
              periodCacheRef.current = {
                vehicleId,
                delayOffsetMs,
                disableUiCap: disableUiCapKey,
                appliedFetchNonce: appliedFetchNonceKey,
                startMs: fetchStartMs,
                endMs: fetchEndMs,
                data,
              };
              setBpsLogData(filterByRangeMs(data, reqStartMs, reqEndMs));
            } else {
              periodCacheRef.current = null;
              setBpsLogData([]);
            }

            lastSuccessfulPeriodRangeRef.current = {
              start: appliedStartInput,
              end: appliedEndInput,
            };
            return;
          } finally {
            endRangeRequest();
          }
        }

        // realtimeの初回/レンジ取得。
        const startIso = formatLocalDateTimeForRequest(startAt);
        const endIso = formatLocalDateTimeForRequest(safeEnd);

        beginRangeRequest();
        try {
          const data = await operatorApiService.doGetBpsLogData({
            vehicleId: vehicleId,
            start: startIso,
            end: endIso,
            maxRows: maxPointsForUi,
            delayOffsetMs,
            noCap: 1,
            signal: abortController.signal,
          });

          if (seq !== fetchSeqRef.current) return;

          if (Array.isArray(data)) {
            setBpsLogData(trimToRealtimeWindowIfNeeded(data, Date.now()));
          } else {
            setBpsLogData([]);
          }
          return;
        } finally {
          endRangeRequest();
        }
      } catch (err) {
        if (isAbortError(err)) return;
        if (seq !== fetchSeqRef.current) return;
        const anyErr = err as any;
        const status = anyErr?.response?.status ?? null;
        if (status === 413) {
          const maxRowsFromApi = anyErr?.response?.data?.max_rows;
          alertTooManyPointsOnce(
            typeof maxRowsFromApi === "number" ? maxRowsFromApi : null,
          );
          if (!isRealtime) rollbackPeriodRangeToLastSuccessful();
          return;
        }
        alertFetchErrorOnce(status);
        console.error("bpslog fetch error:", err);
        setBpsLogData([]);
      }
    };

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [
    vehicleId,
    isRealtime,
    appliedStartInput,
    appliedEndInput,
    appliedFetchNonceKey,
    realtimeWindowMinutes,
    delayOffsetMs,
    disableUiCap,
  ]);

  // リアルタイム更新処理
  useEffect(() => {
    if (!isRealtime) return;
    if (vehicleId === null) return;

    let mounted = true;
    const poll = async () => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      pollAbortRef.current?.abort();
      const abortController = new AbortController();
      pollAbortRef.current = abortController;

      beginPollRequest();
      try {
        const afterId = lastModemIdRef.current;
        if (afterId === null) {
          //　最新IDを取得してカーソルを構える
          const latest = await operatorApiService.doGetBpsLogData({
            vehicleId: vehicleId,
            maxRows: maxPointsForUi,
            delayOffsetMs,
            noCap: disableUiCap ? 1 : undefined,
            signal: abortController.signal,
          });
          if (!mounted) return;
          if (!Array.isArray(latest) || latest.length === 0) return;

          const latestId = latest?.[0]?.modem_id;
          if (typeof latestId !== "number") return;
          lastModemIdRef.current = latestId;

          // データが範囲内に存在する場合は、一度だけ範囲取得
          const now = new Date();
          const realtimeFetchBufferMs = getRealtimeFetchBufferMs(now.getTime());
          const startAt = new Date(
            now.getTime() -
              realtimeWindowMinutes * 60_000 -
              realtimeFetchBufferMs,
          );
          const startIso = formatLocalDateTimeForRequest(startAt);
          const endIso = formatLocalDateTimeForRequest(now);
          const rangeData = await operatorApiService.doGetBpsLogData({
            vehicleId: vehicleId,
            start: startIso,
            end: endIso,
            maxRows: maxPointsForUi,
            delayOffsetMs,
            noCap: 1,
            signal: abortController.signal,
          });
          if (!mounted) return;
          if (Array.isArray(rangeData)) {
            setBpsLogData(trimToRealtimeWindowIfNeeded(rangeData, Date.now()));
          }
          return;
        }

        const data = await operatorApiService.doGetBpsLogData({
          vehicleId: vehicleId,
          afterId,
          maxRows: maxPointsForUi,
          delayOffsetMs,
          noCap: disableUiCap ? 1 : undefined,
          signal: abortController.signal,
        });
        if (!mounted) return;

        setBpsLogData((prev) => {
          const nowMs = Date.now();
          const merged =
            Array.isArray(data) && data.length ? [...prev, ...data] : prev;
          const trimmed = trimToRealtimeWindowIfNeeded(merged, nowMs);
          if (trimmed === merged) return merged;
          return trimmed;
        });
      } catch (err) {
        if (isAbortError(err)) return;
        const anyErr = err as any;
        const status = anyErr?.response?.status ?? null;
        if (status === 413) {
          const maxRowsFromApi = anyErr?.response?.data?.max_rows;
          alertTooManyPointsOnce(
            typeof maxRowsFromApi === "number" ? maxRowsFromApi : null,
          );
          return;
        }
        alertFetchErrorOnce(status);
        console.error("bpslog realtime poll error:", err);
      } finally {
        endPollRequest();
        pollInFlightRef.current = false;
      }
    };

    poll();
    const id = window.setInterval(poll, 1000);
    return () => {
      mounted = false;
      window.clearInterval(id);
      pollAbortRef.current?.abort();
      pollAbortRef.current = null;
      pollInFlightRef.current = false;
    };
  }, [
    isRealtime,
    vehicleId,
    realtimeWindowMinutes,
    delayOffsetMs,
    disableUiCap,
  ]);

  return {
    bpslogData,
    isRangeLoading: rangePendingRequestCount > 0,
    isPollLoading: pollPendingRequestCount > 0,
    isLoading: rangePendingRequestCount > 0 || pollPendingRequestCount > 0,
    cancelRequests,
    rollbackPeriodRangeToLastSuccessful,
  };
};
